// NewSuperAdminForm.js

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/actions';
import { Form, Button, Spinner, Col, FormGroup, Label, Input, Card, CardBody, Alert, InputGroup, InputGroupText } from 'reactstrap';
import { toast } from 'react-toastify';

const roleTranslations = {
    admin: 'مشرف',
    super_admin: 'مشرف عام',
};

const NewSuperAdminForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const { admins } = useSelector(state => state.settings);

    const emailExists = (newEmail) => {
        return admins.some(admin => admin.email === newEmail);
    };

    const validateForm = () => {
        if (!admins || admins.length === 0) {
            toast.error('لا يمكن إرسال النموذج.');
            return false;
        }
        if (emailExists(email)) {
            toast.error('البريد الإلكتروني موجود بالفعل.');
            return false;
        }
        if (!email || !password || !role) {
            toast.error('يرجى ملء جميع الحقول المطلوبة.');
            return false;
        }
        if (password.length < 6) {
            toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل.');
            return false;
        }
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsLoading(true);
        setTimeout(() => {
            const payload = {
                email,
                password,
                role: role,
                notification_token: [],
                username: 'admin'
            };
            dispatch(actions.createSuperAdminRequest(payload));
            setEmail('');
            setPassword('');
            setRole('');
            setIsLoading(false);
            toast.success('تم إنشاء المشرف بنجاح', {
                icon: "✅",
                position: "top-center"
            });
        }, 3000);
    };

    return (
        <Col xl={3} className="mb-4">
            <div className="d-flex align-items-center mb-3">
                <div className="avatar-sm me-3">
                    <span className="avatar-title rounded-circle bg-success-subtle text-success font-size-20">
                        <i className="bx bx-user-plus"></i>
                    </span>
                </div>
                <div>
                    <h5 className="mb-1">إضافة مشرف جديد</h5>
                    <p className="text-muted mb-0">إنشاء حساب مشرف أو مشرف عام</p>
                </div>
            </div>

            <Card className="border shadow-sm" style={{ borderRadius: '12px' }}>
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <FormGroup className="mb-3">
                            <Label className="fw-semibold">
                                <i className="bx bx-envelope me-2"></i>
                                البريد الإلكتروني
                            </Label>
                            <Input
                                type="email"
                                placeholder="admin@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="form-control-lg"
                                style={{ borderRadius: '8px' }}
                                required
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label className="fw-semibold">
                                <i className="bx bx-lock-alt me-2"></i>
                                كلمة المرور
                            </Label>
                            <InputGroup>
                                <Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="form-control-lg"
                                    style={{ borderRadius: '8px 0 0 8px' }}
                                    required
                                />
                                <Button
                                    color="light"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ 
                                        borderRadius: '0 8px 8px 0',
                                        borderLeft: 'none'
                                    }}
                                >
                                    <i className={`bx ${showPassword ? 'bx-hide' : 'bx-show'}`}></i>
                                </Button>
                            </InputGroup>
                            <small className="text-muted">الحد الأدنى 6 أحرف</small>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label className="fw-semibold">
                                <i className="bx bx-shield me-2"></i>
                                الدور الوظيفي
                            </Label>
                            <Input
                                type="select"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="form-control-lg"
                                style={{ borderRadius: '8px' }}
                                required
                            >
                                <option value="" disabled>
                                    اختر الدور
                                </option>
                                <option value="admin">
                                    <i className="bx bx-user me-2"></i>
                                    {roleTranslations.admin}
                                </option>
                                <option value="super_admin">
                                    <i className="bx bx-crown me-2"></i>
                                    {roleTranslations.super_admin}
                                </option>
                            </Input>
                        </FormGroup>

                        {!admins?.length && (
                            <Alert color="warning" className="border-0">
                                <i className="bx bx-error-circle me-2"></i>
                                لا توجد بيانات مشرفين محملة
                            </Alert>
                        )}

                        <Button 
                            color="success" 
                            type="submit" 
                            block 
                            size="lg"
                            className="mt-3"
                            style={{ borderRadius: '8px' }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    جاري الإنشاء...
                                </>
                            ) : (
                                <>
                                    <i className="bx bx-user-plus me-2"></i>
                                    إنشاء مشرف جديد
                                </>
                            )}
                        </Button>
                    </Form>
                </CardBody>
            </Card>

            <Alert color="info" className="mt-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <i className="bx bx-info-circle me-2"></i>
                <strong>ملاحظة:</strong> سيتمكن المشرف من تسجيل الدخول فوراً بعد الإنشاء.
            </Alert>
        </Col>
    );
};

export default NewSuperAdminForm;